import { inject, Injectable } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { FormControlValidators } from '@forms';
import { TranslateService } from '@i18n';
import { FeatureCheckoutTranslationDictionary } from '../../i18n/feature-checkout-dictionary.interface';
import { DirectDebitFormField } from '../../models/direct-debit/direct-debit-form-field.enum';
import { DirectDebitForm } from '../../models/direct-debit/direct-debit-form.interface';

@Injectable()
export class DirectDebitFormBuilder {
  private readonly formBuilder = inject(FormBuilder);
  private readonly translateService = inject(
    TranslateService<FeatureCheckoutTranslationDictionary>,
  );

  build(): FormGroup<DirectDebitForm> {
    return this.formBuilder.group(this.buildFormConfig());
  }

  private buildFormConfig(): DirectDebitForm {
    return {
      [DirectDebitFormField.AccountName]: new FormControl(
        null,
        Validators.compose([
          FormControlValidators.required(
            this.translateService.translate(
              'CHECKOUT_DIRECT_DEBIT_FORM_ACCOUNT_NAME_REQUIRED',
            ),
          ),
          FormControlValidators.maximumLength(
            35,
            this.translateService.translate(
              'CHECKOUT_DIRECT_DEBIT_FORM_ACCOUNT_NAME_MAX_LENGTH',
            ),
          ),
          FormControlValidators.alphabetical(
            this.translateService.translate(
              'CHECKOUT_DIRECT_DEBIT_FORM_ACCOUNT_NAME_ALPHABETICAL',
            ),
          ),
        ]),
      ),
      [DirectDebitFormField.AccountNumber]: new FormControl(
        null,
        Validators.compose([
          FormControlValidators.required(
            this.translateService.translate(
              'CHECKOUT_DIRECT_DEBIT_FORM_ACCOUNT_NUMBER_REQUIRED',
            ),
          ),
          FormControlValidators.minimumLength(
            8,
            this.translateService.translate(
              'CHECKOUT_DIRECT_DEBIT_FORM_ACCOUNT_NUMBER_MIN_LENGTH',
            ),
          ),
          FormControlValidators.pattern(
            /^(\d){8}$/,
            this.translateService.translate(
              'CHECKOUT_DIRECT_DEBIT_FORM_ACCOUNT_NUMBER_VALID',
            ),
          ),
        ]),
      ),
      [DirectDebitFormField.SortCode]: new FormControl(
        null,
        Validators.compose([
          FormControlValidators.required(
            this.translateService.translate(
              'CHECKOUT_DIRECT_DEBIT_FORM_SORTCODE_REQUIRED',
            ),
          ),
          FormControlValidators.minimumLength(
            6,
            this.translateService.translate(
              'CHECKOUT_DIRECT_DEBIT_FORM_SORTCODE_MIN_LENGTH',
            ),
          ),
          FormControlValidators.pattern(
            /^(\d){6}$/,
            this.translateService.translate(
              'CHECKOUT_DIRECT_DEBIT_FORM_SORTCODE_VALID',
            ),
          ),
        ]),
      ),
      [DirectDebitFormField.PaymentDate]: new FormControl(
        null,
        Validators.compose([
          FormControlValidators.required(
            this.translateService.translate(
              'CHECKOUT_DIRECT_DEBIT_FORM_PAYMENT_DATE_REQUIRED',
            ),
          ),
        ]),
      ),
      [DirectDebitFormField.AccountHolderConfirmation]: new FormControl(
        null,
        Validators.compose([
          FormControlValidators.requiredTrue(
            this.translateService.translate(
              'CHECKOUT_DIRECT_DEBIT_FORM_ACCOUNT_HOLDER_CONFIRMATION_REQUIRED_TRUE',
            ),
          ),
        ]),
      ),
      [DirectDebitFormField.TermsAndConditions]: new FormControl(
        null,
        Validators.compose([
          FormControlValidators.requiredTrue(
            this.translateService.translate(
              'CHECKOUT_DIRECT_DEBIT_FORM_TERMS_AND_CONDITIONS_REQUIRED_TRUE',
            ),
          ),
        ]),
      ),
      [DirectDebitFormField.PaperlessPreference]: new FormControl(null),
    };
  }
}
